import { IProgressAuditFullContext } from "../../../features/progressAudit/progressAuditFullSlice";

const EPSILON = 1e-6;

export type CountersStatus = "inProgress" | "ok" | "failed";

interface RawCourse {
  _id: string;
  title: string;
  topics: string[];
  evaluation?: string;
  survey?: string;
  certificate?: string;
}
interface RawTopic {
  _id: string;
  title: string;
  lessons: string[];
  evaluation?: string;
}
interface RawLesson {
  _id: string;
  title: string;
  evaluation?: string;
}
interface RawLessonProgress {
  _id: string;
  lessonId: string;
  progress: number;
}
interface RawEvaluationPassed {
  evaluationId: string;
}
interface RawTopicProgress {
  topicId: string;
  progress: number;
}
interface RawProgress {
  lessons?: RawLessonProgress[];
  topics?: RawTopicProgress[];
  evaluationsPassed?: RawEvaluationPassed[];
  courseProgress?: { progress: number };
}

export interface LessonCounter {
  lessonId: string;
  title: string;
  hasQuiz: boolean;
  quizEvaluationId?: string;
  approved: boolean;
  // The progress.lessons[] subdocument's own _id (not lessonId) - needed
  // to call the admin "fix" endpoint, which identifies the entry this
  // way since the same lesson could in theory be listed under more than
  // one topic. Undefined if the student never started this lesson at
  // all (no subdocument exists yet to fix).
  progressLessonSubdocId?: string;
}

export interface TopicCounter {
  topicId: string;
  title: string;
  lessonsRequired: number;
  lessonsApproved: number;
  lessons: LessonCounter[];
  hasTest: boolean;
  testEvaluationId?: string;
  testPassed: boolean;
  // The raw progress.topics[].progress for this topic - the field the
  // makeProgress bug could (before the fix) set to 1 without lessons/
  // evaluation actually being confirmed. Shown as-is (not the recomputed
  // "really complete" state) so an admin can see when it disagrees with
  // lessonsApproved/testPassed - the lesson "Fix" button only makes sense
  // (and only shows) when this is already 1, since that's exactly the bug
  // scenario: the topic looks finished but something inside it isn't.
  markedFinished: boolean;
  // Whether the admin "Fix" for this topic's test should be offered: the
  // test isn't in evaluationsPassed, and either the topic is already
  // marked finished (the original bug) or every lesson is approved - the
  // opposite case, where the student passed the test (EvaluationResult
  // exists) but the write adding it to evaluationsPassed was lost, so
  // service-learning keeps rejecting the topic and it stays < 1 forever
  // (SYSTEM.debt.md ítem 29/§15.29). The backend fix endpoint still
  // checks for a passed EvaluationResult before changing anything.
  canFixTest: boolean;
}

export interface CourseCounters {
  status: CountersStatus;
  // progress.courseProgress.progress > ~1 - i.e. the course is marked
  // finished. A finished course should always have gotten a certificate
  // (service-learning only sets finishedAt/progress:1 after createCertificate
  // succeeds) - CountersModal uses this to flag one that doesn't as a data
  // inconsistency, independent of computedPercentage/status (which is
  // about whether the underlying items were *really* completed, not
  // about the certificate itself).
  isFinished: boolean;
  // !!course.certificate - whether the course even has a certificate
  // template configured at all. It's optional (service-learning's Course
  // model), so a finished course with no certificate isn't always a bug -
  // only when this is true too (CountersModal gates both the missing-
  // certificate alert and the admin "create" button on it).
  hasCertificateTemplate: boolean;
  courseTitle: string;
  computedPercentage: number;
  totalItems: number;
  completedItems: number;
  topics: TopicCounter[];
  hasExam: boolean;
  examEvaluationId?: string;
  examPassed: boolean;
  hasSurvey: boolean;
  surveyEvaluationId?: string;
  surveyPassed: boolean;
}

// Mirrors, item for item, the percentage formula service-learning computes
// server-side in getCourseWithExtra (controllers/utils/index.ts) - lessons
// finished (from progress.lessons, never from progress.topics[].progress,
// which is exactly the field the makeProgress bug can set to 1 without the
// topic's evaluation being passed) + topic tests passed (evaluationsPassed)
// + the course exam passed (evaluationsPassed) - course.survey is excluded
// from the percentage on purpose, same as the backend formula.
export const computeCourseCounters = (full: IProgressAuditFullContext): CourseCounters => {
  const course = full.course as unknown as RawCourse | null;
  const topicsList = (full.topics || []) as unknown as RawTopic[];
  const lessonsList = (full.lessons || []) as unknown as RawLesson[];
  const progress = full.progress as unknown as RawProgress | null;

  const topicsDict: Record<string, RawTopic> = {};
  topicsList.forEach((topic) => { topicsDict[topic._id] = topic; });
  const lessonsDict: Record<string, RawLesson> = {};
  lessonsList.forEach((lesson) => { lessonsDict[lesson._id] = lesson; });
  const lessonProgressDict: Record<string, number> = {};
  const lessonSubdocIdDict: Record<string, string> = {};
  (progress?.lessons || []).forEach((lessonProgress) => {
    lessonProgressDict[lessonProgress.lessonId] = lessonProgress.progress;
    lessonSubdocIdDict[lessonProgress.lessonId] = lessonProgress._id;
  });
  const evaluationsPassed = new Set(
    (progress?.evaluationsPassed || []).map((evaluationPassed) => evaluationPassed.evaluationId)
  );
  const topicProgressDict: Record<string, number> = {};
  (progress?.topics || []).forEach((topicProgress) => {
    topicProgressDict[topicProgress.topicId] = topicProgress.progress;
  });

  let totalOfLessons = 0;
  let totalOfTopicsTests = 0;
  let completedLessons = 0;
  let completedTopicsTests = 0;

  const topics: TopicCounter[] = (course?.topics || []).map((topicId) => {
    const topic = topicsDict[topicId];
    const lessons: LessonCounter[] = (topic?.lessons || []).map((lessonId) => {
      const lesson = lessonsDict[lessonId];
      totalOfLessons += 1;
      const approved = (lessonProgressDict[lessonId] ?? 0) > 1 - EPSILON;
      if (approved) {
        completedLessons += 1;
      }
      return {
        lessonId,
        title: lesson?.title || lessonId,
        hasQuiz: !!lesson?.evaluation,
        quizEvaluationId: lesson?.evaluation,
        approved,
        progressLessonSubdocId: lessonSubdocIdDict[lessonId],
      };
    });
    const hasTest = !!topic?.evaluation;
    if (hasTest) {
      totalOfTopicsTests += 1;
    }
    const testPassed = !!topic?.evaluation && evaluationsPassed.has(topic.evaluation);
    if (testPassed) {
      completedTopicsTests += 1;
    }
    const lessonsApproved = lessons.filter((lesson) => lesson.approved).length;
    const markedFinished = (topicProgressDict[topicId] ?? 0) > 1 - EPSILON;
    return {
      topicId,
      title: topic?.title || topicId,
      lessonsRequired: lessons.length,
      lessonsApproved,
      lessons,
      hasTest,
      testEvaluationId: topic?.evaluation,
      testPassed,
      markedFinished,
      canFixTest: hasTest && !testPassed && (markedFinished || lessonsApproved === lessons.length),
    };
  });

  const hasExam = !!course?.evaluation;
  const examPassed = !!course?.evaluation && evaluationsPassed.has(course.evaluation);
  const hasSurvey = !!course?.survey;
  const surveyPassed = !!course?.survey && evaluationsPassed.has(course.survey);

  const totalOfCourseExamSurvey = hasExam ? 1 : 0;
  const completedCourseExamSurvey = examPassed ? 1 : 0;

  const totalItems = totalOfLessons + totalOfTopicsTests + totalOfCourseExamSurvey;
  const completedItems = completedLessons + completedTopicsTests + completedCourseExamSurvey;
  const computedPercentage = totalItems > 0 ? completedItems / totalItems : 0;

  const isFinished = (progress?.courseProgress?.progress ?? 0) > 1 - EPSILON;
  const status: CountersStatus = !isFinished
    ? "inProgress"
    : computedPercentage > 1 - EPSILON
      ? "ok"
      : "failed";

  return {
    status,
    isFinished,
    hasCertificateTemplate: !!course?.certificate,
    courseTitle: course?.title || "",
    computedPercentage,
    totalItems,
    completedItems,
    topics,
    hasExam,
    examEvaluationId: course?.evaluation,
    examPassed,
    hasSurvey,
    surveyEvaluationId: course?.survey,
    surveyPassed,
  };
};
